/**
 * Dotted-version comparison, shared.
 *
 * Two private copies of this existed before — one in the console for "is an update available"
 * badging, one inside the rule engine for vulnerability range matching. A third was about to be
 * written for connector-downgrade detection, which is how a codebase ends up with three subtly
 * different opinions about whether `0.4.10` is newer than `0.4.9`.
 *
 * Deliberately NOT a full semver implementation. Connector releases are plain `x.y.z`; this handles
 * that plus the noise that actually turns up: uneven lengths (`1.2` vs `1.2.0`), a leading `v`, and
 * pre-release/build suffixes compared by their numeric core. Enough for ordering our own releases,
 * and honest about not being more.
 *
 * The rule engine keeps its own copy for now: its comparator drives virtual-patch range matching,
 * where a semantics change carries real risk, and consolidating it deserves its own slice.
 */

/**
 * Split a version into its numeric core and whether it carries a pre-release suffix.
 *
 * `+` is handled BEFORE `-`, and that order matters: build metadata may itself contain a dash
 * (`1.2.3+build-7`), and splitting on `-` first would read that as a pre-release and rank a plain
 * release below itself.
 */
function split(v: string): { core: string; pre: boolean } {
  // Leading non-digits go first, so `v1.2.3` == `1.2.3`.
  const stripped = v.trim().replace(/^[^\d]*/, '');
  // Build metadata is not part of precedence (semver §10) — discarded entirely, never a rank signal.
  const beforeBuild = stripped.split('+')[0] ?? '';
  const dash = beforeBuild.indexOf('-');
  if (dash === -1) return { core: beforeBuild, pre: false };
  // A trailing bare `-` is not a pre-release; there has to be something after it.
  return { core: beforeBuild.slice(0, dash), pre: beforeBuild.length > dash + 1 };
}

/** Numeric core of a version string: `v1.2.3-beta.1` → `1.2.3`. Empty when there is no number. */
function core(v: string): string {
  return split(v).core;
}

/**
 * Does this string contain a version at all?
 *
 * `core()` strips leading non-digits, so a string with no digits collapses to `''` and then parses
 * as `[0]` — older than everything. Without this guard `isVersionRegression` reported **any**
 * malformed version as a downgrade, manufacturing the exact false incident it exists to avoid. A
 * security signal that fires on garbage is one nobody trusts.
 */
function isParseable(v: string): boolean {
  return core(v).length > 0;
}

/** Numeric core as components: `v1.2.3-beta.1` → `[1, 2, 3]`. */
function parts(v: string): number[] {
  return core(v)
    .split('.')
    .map((p) => Number.parseInt(p, 10) || 0);
}

/**
 * `-1` when `a` is older, `1` when newer, `0` when equal.
 *
 * **The only version comparator (ADR-0031).** Three copies of this existed, holding two different
 * opinions, and the two that mattered sat on either side of one decision: `vpatch-scope` decided
 * which sites a managed vpatch reached, `rule-engine` decided whether it then fired. A site on
 * `2.3.1-beta1` was delivered a `< 2.3.1` patch that could never trigger, while the sync annotation
 * reported it shielded — the failure mode that produces false confidence rather than silence.
 *
 * **A pre-release ranks BELOW its release** (`2.3.1-beta` < `2.3.1`), which is the one contestable
 * call and rests on the two errors not being symmetric. Treating a pre-release as fixed withdraws
 * protection from a site that is still vulnerable, invisibly, while telling it that it is covered.
 * Treating it as affected applies a patch that may be unnecessary — visible, measurable, and
 * reversible by publishing a corrected range. A pre-release genuinely precedes its release, so
 * assuming the fix landed is assuming the favourable answer to an unknowable question. `core 7.1-RC2`
 * was on the fleet when this was written.
 *
 * Deliberately still not full semver: pre-release **identifiers** are not compared, so `-beta` and
 * `-rc` rank equal. Affected ranges are authored against releases, never against pre-releases, so
 * ordering two pre-releases of one version is a question the system never asks. That is a stated
 * non-goal, not an omission.
 */
export function compareVersions(a: string, b: string): number {
  const va = split(a);
  const vb = split(b);
  const pa = parts(a);
  const pb = parts(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  // Only reached when the numeric cores are identical, which bounds the blast radius of this rule
  // to exact ties — `2.4.0-beta` vs `2.3.1` is settled by the numbers and never consults `pre`.
  if (va.pre === vb.pre) return 0;
  return va.pre ? -1 : 1;
}

/** One comparator from an affected-version range, e.g. `{ op: '<=', ver: '2.3.1' }`. */
export interface VersionComparator {
  op: string;
  ver: string;
}

/**
 * Parse an affected-version range into ANDed comparators (ADR-0031 §1).
 *
 * **The only range parser.** Two existed, and their disagreements were worse than the comparator's:
 *
 *  - `vpatch-scope`'s regex captured ONE optional operator and treated the rest as a version, so
 *    `">= 2.3.0 <= 2.3.1"` — the shape most real advisories take — parsed as `>=` against the
 *    literal string `"2.3.0 <= 2.3.1"`, whose numeric parse is nonsense. It did not fail; it
 *    silently scoped the wrong set of sites.
 *  - The rule engine required an explicit operator, so a bare `"2.3.1"` (an advisory naming one bad
 *    release) yielded nothing and the rule never fired, while delivery read the same string as `=`
 *    and shipped the patch.
 *
 * Each produced the ADR-0031 failure by a different route: a vpatch delivered, counted as coverage,
 * and unable to fire.
 *
 * Returns `null` when nothing parses — see `satisfiesVersionRange` for what that means.
 */
export function parseVersionRange(range: string): VersionComparator[] | null {
  const comparators: VersionComparator[] = [];
  // `v?` because `compareVersions` strips a leading `v`; refusing to parse what it can compare is
  // how `<= v2.3.1` became a range that delivered but never fired.
  const re = /(<=|>=|==|<|>|=)\s*(v?[0-9][0-9A-Za-z.\-+]*)/g;
  let m: RegExpExecArray | null = re.exec(range);
  while (m !== null) {
    comparators.push({ op: m[1] as string, ver: m[2] as string });
    m = re.exec(range);
  }
  if (comparators.length === 0) {
    // A bare version means exact match: an advisory naming a single bad release.
    const bare = range.trim();
    if (/^v?[0-9][0-9A-Za-z.\-+]*$/.test(bare)) comparators.push({ op: '=', ver: bare });
  }
  return comparators.length > 0 ? comparators : null;
}

/**
 * Is `installed` inside `range`? All comparators must hold (AND).
 *
 * **An unparseable range matches nothing.** The two sides used to fail in opposite directions —
 * delivery returned `true` ("never silently drop protection"), the engine returned `false` — which
 * combined into the worst of both: delivered, counted as coverage, inert.
 *
 * Fail-closed is forced by golden rule #5: enforcement is default-allow, and an unknown construct
 * must never block a site. An unparseable range means the affected set is unknown, and blocking on
 * an unknown is the one thing the rule forbids.
 *
 * The honest cost: a malformed catalog entry now drops protection silently rather than
 * over-delivering. That is a **visibility** problem, and it belongs at authoring time — a range that
 * cannot be parsed should be rejected when it is written, not reinterpreted when it is read.
 */
export function satisfiesVersionRange(installed: string, range: string): boolean {
  const comparators = parseVersionRange(range);
  if (!comparators) return false;
  return comparators.every(({ op, ver }) => {
    const c = compareVersions(installed, ver);
    switch (op) {
      case '<':
        return c < 0;
      case '<=':
        return c <= 0;
      case '>':
        return c > 0;
      case '>=':
        return c >= 0;
      case '=':
      case '==':
        return c === 0;
      default:
        return false;
    }
  });
}

/** True when `installed` is strictly older than `latest`. Missing input ⇒ false. */
export function isOutdated(installed: string | undefined, latest: string | undefined): boolean {
  if (!installed || !latest) return false;
  return compareVersions(installed, latest) < 0;
}

/**
 * Did a site's reported agent version go **backwards**?
 *
 * Never routine. It means either a deploy overwrote the plugin with an older committed copy — which
 * happened on a real site on 2026-08-03 and is an ordinary consequence of keeping wp-content in
 * version control — or somebody downgraded the agent deliberately.
 *
 * The platform cannot distinguish those, and should not pretend to. But it must not miss the
 * transition: silently downgrading a security agent is a standard early step in an intrusion, and a
 * downgrade re-opens every vulnerability fixed in between. It went unnoticed until someone happened
 * to be reading version numbers.
 *
 * Returns false when either side is missing: a site reporting for the first time has not regressed,
 * and an unparseable version must not manufacture an incident.
 */
export function isVersionRegression(
  previous: string | undefined | null,
  reported: string | undefined | null,
): boolean {
  if (!previous || !reported) return false;
  // Either side unreadable ⇒ not a regression. See `isParseable`: without this, a malformed version
  // parses as zero and every garbage report looks like a downgrade.
  if (!isParseable(previous) || !isParseable(reported)) return false;
  return compareVersions(reported, previous) < 0;
}
